<script lang="ts" setup>
import { faLock } from "@fortawesome/pro-regular-svg-icons";

const errors = ref<Record<string, string[]>>();
const anyChanges = ref<boolean>(false);
const submitting = ref<boolean>(false);
const success = ref<boolean>(false);
const changePassword = ref<boolean>(false);
const api = useApi();

const { user: auth, refreshIdentity } = useAuth();

const user = reactive({
    first_name: auth.value.first_name,
    last_name: auth.value.last_name,
    email: auth.value.email,
    phone: auth.value.phone,
    password: "",
    password_confirmation: "",
    avatar: undefined as any,
});

watch(user, () => {
    if (Object.keys(getChangedValues()).length > 0) {
        anyChanges.value = true;
    } else {
        anyChanges.value = false;
    }
});

async function resetData() {
    user.avatar = undefined;
    user.first_name = auth.value.first_name;
    user.last_name = auth.value.last_name;
    user.email = auth.value.email;
}

function getChangedValues() {
    const changedValues: any = {};

    for (const key in user) {
        if (
            auth.value[key] !== user[key as keyof typeof user] &&
            user[key as keyof typeof user]
        ) {
            changedValues[key] = user[key as keyof typeof user];
        }
    }

    return changedValues;
}

async function getFileObject(event: Event) {
    const _file = (event.target as HTMLInputElement).files as FileList;
    // TODO File upload
    user.avatar = _file[0];
}

async function update() {
    try {
        const form = new FormData();
        errors.value = undefined;
        submitting.value = true;
        success.value = false;

        await api.users.update(auth.value.id, getChangedValues());
        submitting.value = false;
        success.value = true;
        anyChanges.value = false;

        if (changePassword.value) {
            useToast().add({
                title: "You've successfully reset your password.",
            });
        } else {
            await refreshIdentity();
            useToast().add({
                title: "Settings updated!",
            });
        }

        changePassword.value = false;

        // Re-fetch user
        await resetData();
    } catch (e: any) {
        const unmatched_password_message =
            "The password field confirmation does not match.";
        const errorMessages = e?.errors || [];
        if (
            errorMessages?.password &&
            errorMessages?.password.includes(unmatched_password_message)
        ) {
            errorMessages["password_confirmation"] = [
                unmatched_password_message,
            ];
            errorMessages["password"] = errorMessages.password.filter(function (
                er: string,
            ) {
                return er !== unmatched_password_message;
            });
        }
        errors.value = errorMessages;
        submitting.value = false;
    }
}

const avatar = ref();

const file = computed(() => user.avatar);
const avatarUrl = useObjectUrl(file);
</script>

<template>
    <AppSettingsContent
        description="Manage your profile and settings"
        title="Profile"
    >
        <AppSettingsForm
            v-slot="{ formGroupUi }"
            :state="user"
            description="Update or change your personal information here."
            title="Personal Information"
            @submit="update"
        >
            <UFormGroup
                :error="
                    errors && errors.avatar
                        ? errors.avatar.join(', ')
                        : undefined
                "
                :ui="{ container: 'flex items-center gap-x-8 pt-1' }"
                class="col-span-full"
                label="Avatar"
            >
                <HdImageInput
                    :src="user.avatar ?? auth.avatar"
                    @update:src="user.avatar = $event"
                    :size="100"
                    round
                    type="avatar"
                    :icon-height="24"
                    :pintura-aspect-ratio="1"
                    class="rounded-full max-h-20 !min-w-20 !max-w-20"
                    image-class="object-cover"
                    v-slot="{ trigger }"
                >
                    <div>
                        <UButton color="gray" size="lg" @click="trigger">
                            Change Avatar
                        </UButton>

                        <p class="mt-1 text-xs leading-5 text-gray-400">
                            Any web supported image file is allowed.
                        </p>
                    </div>
                </HdImageInput>
            </UFormGroup>
            <UFormGroup
                :error="
                    errors && errors.first_name
                        ? errors.first_name.join(', ')
                        : undefined
                "
                :ui="formGroupUi"
                class="sm:col-span-3"
                label="First name"
                name="first_name"
                size="lg"
            >
                <UInput v-model="user.first_name" />
            </UFormGroup>
            <UFormGroup
                :error="
                    errors && errors.last_name
                        ? errors.last_name.join(', ')
                        : undefined
                "
                :ui="formGroupUi"
                class="sm:col-span-3"
                label="Last name"
                name="last_name"
                size="lg"
            >
                <UInput v-model="user.last_name" />
            </UFormGroup>
            <UFormGroup
                :error="
                    errors && errors.email ? errors.email.join(', ') : undefined
                "
                :ui="formGroupUi"
                class="sm:col-span-3"
                label="Email"
                name="email"
                size="lg"
            >
                <UInput v-model="user.email" />
            </UFormGroup>
            <UFormGroup
                :error="
                    errors && errors.email ? errors.email.join(', ') : undefined
                "
                :ui="formGroupUi"
                class="sm:col-span-3"
                label="Phone"
                name="phone"
                size="lg"
            >
                <UInput v-model="user.phone" />
            </UFormGroup>
            <div class="col-span-full mt-3">
                <UButton
                    :color="anyChanges && !changePassword ? 'primary' : 'gray'"
                    :disabled="submitting || !anyChanges || changePassword"
                    :loading="submitting && anyChanges && !changePassword"
                    size="lg"
                    type="submit"
                >
                    Save Settings
                </UButton>
            </div>
        </AppSettingsForm>

        <AppSettingsForm
            v-slot="{ formGroupUi }"
            :state="user"
            description="Update your password associated with your account."
            title="Change password"
            @submit="update"
        >
            <UFormGroup
                :error="
                    errors && errors.password
                        ? errors.password.join(', ')
                        : undefined
                "
                :ui="formGroupUi"
                class="col-span-full"
                label="New Password"
                name="password"
                size="lg"
            >
                <UInput
                    v-model="user.password"
                    placeholder="Input new password"
                    type="password"
                    @change="changePassword = true"
                />
            </UFormGroup>
            <UFormGroup
                :error="
                    errors && errors.password_confirmation
                        ? errors.password_confirmation.join(', ')
                        : undefined
                "
                :ui="formGroupUi"
                class="col-span-full"
                label="Confirm Password"
                name="password_confirmation"
                size="lg"
            >
                <UInput
                    v-model="user.password_confirmation"
                    placeholder="••••••••••"
                    type="password"
                    @change="changePassword = true"
                />
            </UFormGroup>
            <div class="col-span-full mt-3">
                <UButton
                    :color="changePassword ? 'primary' : 'gray'"
                    :disabled="submitting || !changePassword"
                    :loading="submitting && changePassword"
                    size="lg"
                    type="submit"
                >
                    <FontAwesomeIcon :icon="faLock" />
                    Change Password
                </UButton>
            </div>
        </AppSettingsForm>
    </AppSettingsContent>
</template>
